import React, { useEffect, useState, useMemo } from 'react';
import Box from '@mui/material/Box';
import { enqueueSnackbar } from 'notistack';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import { Typography } from '@mui/material';
import MapGL from '../../Map/Map';
import { useAppDispatch, useAppSelector } from '../../../state/store';
import { getCurrentLocation, getSensorsById } from '../../../state/selectors';
import { setSensorsById } from '../../../state/actions';
import { MapCentrePosition, SpaceParams } from '../../Map/mapHelpers';
import TaggedSensorsList from './TaggedSensorsList';
import SensorsInLoc from './SensorsInLoc';
import LocationSwitcher from '../../../components/LocationSwitcher';
import useGlobalStyles from '../../../styles';
import useStyles from '../../../styles/tags';
import useSensorDetailsInLoc from './useSensorDetailsInLoc';
import TagNameForms from './TagNameForms';
import { themeProps } from '../../../styles/theme';
import { fetchSensorLatest, updateSensorProperties } from '../../../services/apiService';

interface TagConfigDialogProps {
  showDialog: boolean;
  closeDialog: () => void;
  selectedTag: string;
  setSelectedTag: (value: string) => void;
  allSpaces: SpaceParams[] | undefined;
}

function TagConfigDialog({
  showDialog,
  closeDialog,
  selectedTag,
  setSelectedTag,
  allSpaces,
}: TagConfigDialogProps) {
  const dispatch = useAppDispatch();
  const globalClasses = useGlobalStyles();
  const classes = useStyles();
  const currentLocation = useAppSelector(getCurrentLocation);
  const sensorsById = useAppSelector(getSensorsById);
  const [taggedSensors, setTaggedSensors] = useState<string[]>([]);
  const sensorsDetailsInLoc = useSensorDetailsInLoc();

  const existingTaggedSensors = useMemo(
    () =>
      sensorsDetailsInLoc
        .filter((sensor) => sensor.tags?.some((item) => item === selectedTag))
        .map((item) => item.id),
    [selectedTag, sensorsDetailsInLoc]
  );

  // set tagged sensors from store for initial landing
  useEffect(() => {
    if (selectedTag) {
      const sensorsWithTag = sensorsDetailsInLoc.filter((sensor) =>
        sensor.tags?.some((item) => item === selectedTag)
      );

      if (sensorsWithTag.length > 0) {
        const sensorsList = sensorsWithTag.map((item) => item.id);
        setTaggedSensors(sensorsList);
      } else setTaggedSensors([]);
    } else setTaggedSensors([]);
  }, [currentLocation, selectedTag, sensorsById, sensorsDetailsInLoc]);

  // add or remove sensors locally
  const handleTagging = (sensorId: string) => {
    const isExistingId = taggedSensors.find((id) => id === sensorId);
    if (isExistingId) setTaggedSensors(taggedSensors.filter((id) => id !== sensorId));
    else setTaggedSensors([...taggedSensors, sensorId]);
  };

  // update tag form sensor props
  const handleSubmit = () => {
    if (selectedTag && JSON.stringify(existingTaggedSensors) !== JSON.stringify(taggedSensors)) {
      const removedSensors = existingTaggedSensors.filter(
        (sensorId) => !taggedSensors.includes(sensorId)
      );

      // Remove group Tag from sensors that are no longer in the group
      removedSensors.forEach(async (sensorId) => {
        const sensor = await fetchSensorLatest(sensorId);
        const updatedTags = sensor?.tags?.filter((tag) => tag !== selectedTag);
        updateSensorProperties(sensor.id, { tags: updatedTags }).then((response) => {
          if (response.id) {
            const updatedSensorDetails = { ...sensor, ...response };
            dispatch(setSensorsById([updatedSensorDetails]));
          }
        });
      });

      // Add group tag if missing for selected sensors
      taggedSensors.forEach(async (sensorId) => {
        const sensor = await fetchSensorLatest(sensorId);
        const allTags = sensor.tags || [];
        const newTags = allTags.includes(selectedTag) ? allTags : [...allTags, selectedTag];

        updateSensorProperties(sensor.id, { tags: newTags }).then((response) => {
          if (response.id) {
            const updatedSensorDetails = { ...sensor, ...response };
            dispatch(setSensorsById([updatedSensorDetails]));
          }
        });
      });

      closeDialog();
      setTaggedSensors([]);
      enqueueSnackbar(`Tag successfully ${selectedTag ? 'Updated' : 'Created'}`, {
        variant: 'success',
      });
    } else if (!selectedTag) {
      enqueueSnackbar('Tag name is missing', {
        variant: 'error',
      });
    } else if (JSON.stringify(existingTaggedSensors) === JSON.stringify(taggedSensors)) {
      enqueueSnackbar('No changes detected', {
        variant: 'error',
      });
    }
  };

  return (
    <Dialog
      open={showDialog}
      onClose={closeDialog}
      fullScreen
      PaperProps={{
        sx: {
          overflow: 'unset',
        },
      }}
      sx={{ padding: '2rem' }}
    >
      <Box
        sx={{
          position: 'relative',
          height: '100%',
        }}
      >
        <MapGL
          mapCentrePosition={MapCentrePosition.right}
          taggedSensors={taggedSensors}
          handleTagging={handleTagging}
          showTaggedSpaces
        />
        <IconButton aria-label="close" onClick={closeDialog} className={classes.configCloseBtn}>
          <CloseIcon />
        </IconButton>
        <Box sx={{ position: 'absolute', top: 10, left: 50, minWidth: '490px' }}>
          <LocationSwitcher />
        </Box>
        <Box className={classes.configContainer}>
          <Box className={globalClasses.compareListPanel}>
            <Box className={globalClasses.compareList} sx={{ borderRadius: '8px' }}>
              <Box className={globalClasses.spaceBetweenFlex}>
                <Typography variant="h5">Tag Config</Typography>
                <Box className={globalClasses.centeredFlex}>
                  <Button
                    size="small"
                    variant={themeProps.btnVariant.default}
                    onClick={handleSubmit}
                  >
                    Submit
                  </Button>
                  <Button
                    size="small"
                    variant={themeProps.btnVariant.default}
                    onClick={closeDialog}
                    sx={{ marginLeft: '5px' }}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
              <TagNameForms
                selectedTag={selectedTag}
                setSelectedTag={setSelectedTag}
                existingTaggedSensors={existingTaggedSensors}
                allSpaces={allSpaces}
              />
              <TaggedSensorsList
                taggedSensors={taggedSensors} // WIP sensors that are being added/updated
                existingTaggedSensors={existingTaggedSensors} // sensors that already exists before any edit or update
                handleTagging={handleTagging}
              />
            </Box>
          </Box>

          <Box className={globalClasses.compareListPanel}>
            <Box className={globalClasses.compareList} sx={{ borderRadius: '8px' }}>
              <SensorsInLoc
                taggedSensors={[
                  ...existingTaggedSensors,
                  ...taggedSensors.filter((item) => !existingTaggedSensors.includes(item)),
                ]}
                handleTagging={handleTagging}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}

export default TagConfigDialog;
